/**
 * Der Chat (MEIN-TAG-v15.md Kapitel 12).
 *
 * Ein Panel von rechts, zweispaltig: links die Chats, rechts der Verlauf –
 * der Aufbau, den jeder von WhatsApp kennt. Es ist ein Bauteil der Shell und
 * öffnet auf jeder Maske über den Nachrichten-Knopf; es gibt keine zweite
 * Fassung je Seite.
 *
 * Zwei Dinge bestimmen den Aufbau dieser Datei:
 *
 *  - **Der ganze Verlauf ist da** (12.8). Er wird nie am Stück geladen,
 *    sondern in Seiten zu 30 Nachrichten über einen Cursor. Beim Nachladen
 *    darf die Ansicht nicht springen – deshalb wird vor dem Einfügen der
 *    Abstand zum unteren Rand gemerkt und danach wiederhergestellt.
 *  - **Gesucht wird am Server** (12.9, 12.10), über einen Volltextindex.
 *    Im Browser zu filtern hieße, erst alles zu laden – genau das, was 12.8
 *    verbietet.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api';
import { useKora } from '../zustand';
import { ChatVerfasser, type Anhangwahl } from './ChatVerfasser';
import { Gruppeninfo } from './Gruppeninfo';
import type { ChatUeberblick } from '../typen';
import './chat.css';

/* ---- Was der Server liefert ------------------------------------------------ */

export interface ChatNachricht {
  id: number;
  autor: string;
  autorKurz: string;
  eigen: boolean;
  zeitpunkt: string;
  tag: string;
  text: string;
  angepinnt: boolean;
  ausMonday: boolean;
  erwaehnt: string[];
  antwortAuf: { id: number; autor: string; anfang: string } | null;
  gelesenVonAllen: boolean;
  reaktionen: { emoji: string; wer: string[] }[];
  anhaenge: { dateiname: string; url: string; ort: string }[];
}

interface VerlaufAntwort {
  nachrichten: ChatNachricht[];
  nochOffen: number;
  beginn: string | null;
  neuAb: number | null;
  angepinnt: ChatNachricht | null;
  angepinntVon: string;
}

export interface ChatZeile {
  raum: string;
  art: 'gruppe' | 'direkt';
  name: string;
  kuerzel: string;
  symbol: string;
  bildUrl: string;
  istTeam: boolean;
  online: boolean;
  vorschau: string;
  vorschauWer: string;
  eigenesLetztes: boolean;
  letzteGelesen: boolean;
  zeitpunkt: string | null;
  ungelesen: number;
}

interface ChatListe {
  gruppen: ChatZeile[];
  direkte: ChatZeile[];
  ungelesenGesamt: number;
}

interface Treffer {
  id: number;
  raum: string;
  ort: string;
  autor: string;
  zeitpunkt: string;
  ausschnitt: string;
  von: number;
  bis: number;
}

export interface Gruppe {
  id: number;
  raum: string;
  name: string;
  beschreibung: string;
  symbol: string;
  bildUrl: string;
  istTeam: boolean;
  mitglieder: string[];
}

/* ---- Datum und Zeit -------------------------------------------------------- */

const UHR = new Intl.DateTimeFormat('de-AT', { hour: '2-digit', minute: '2-digit' });
const WOCHENTAG = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

const heuteIso = () => new Date().toLocaleDateString('sv-SE');

function tagVor(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toLocaleDateString('sv-SE');
}

/** „Heute", „Gestern", Wochentag innerhalb einer Woche, sonst voll (12.7). */
function tagLabel(iso: string): string {
  if (iso === heuteIso()) return 'Heute';
  if (iso === tagVor(1)) return 'Gestern';
  /*
   * Davor das genaue Datum – nicht der bloße Wochentag.
   *
   * „Montag" ist nur so lange eine Auskunft, wie man ihn noch im Kopf hat;
   * in einem Verlauf, der Monate zurückreicht, gibt es viele Montage. Wie
   * im Messenger: Heute, Gestern, sonst das Datum.
   */
  const d = new Date(`${iso}T12:00:00`);
  return `${WOCHENTAG[d.getDay()]}, ${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

/** Die Zeit in der Chat-Liste: heute die Uhr, davor Wochentag oder Datum (12.3). */
function listenZeit(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const tag = d.toLocaleDateString('sv-SE');
  if (tag === heuteIso()) return UHR.format(d);
  if (tag === tagVor(1)) return 'Gestern';
  /* Dieselbe Regel wie im Verlauf: davor steht das Datum, kein Wochentag. */
  return d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

const uhrzeit = (iso: string) => UHR.format(new Date(iso));

/**
 * Die Namensfarbe im Team-Chat (12.6).
 *
 * Feste Zuordnung aus den dunklen Textvarianten der Prozesstöne – keine
 * gewürfelte Farbe je Sitzung, sonst heißt Grün morgen jemand anderes.
 */
const NAMENSFARBEN: Record<string, string> = {
  Tibor: '#4A564C',
  Jana: '#6F6154',
  Michal: '#565D59',
  Eveline: '#8C4A16',
};
/* Wer nicht in der Liste steht, bekommt aus demselben Vorrat eine feste Farbe. */
const WEITERE = ['#4F6268', '#8E1300', '#6F6154', '#4A564C'];
function namensfarbe(name: string): string {
  const gesetzt = NAMENSFARBEN[name];
  if (gesetzt) return gesetzt;
  let summe = 0;
  for (let i = 0; i < name.length; i++) summe = (summe * 31 + name.charCodeAt(i)) % 9973;
  return WEITERE[summe % WEITERE.length]!;
}

/* ---- Das Panel ------------------------------------------------------------- */

export function ChatPanel({ onSchliessen }: { onSchliessen: () => void }) {
  const { nutzer } = useKora();
  const queryClient = useQueryClient();
  const ich = nutzer?.name ?? '';

  const [raum, setRaum] = useState('team');
  const [filter, setFilter] = useState<'alle' | 'ungelesen'>('alle');
  const [listenSuche, setListenSuche] = useState('');
  const [gruppeOffen, setGruppeOffen] = useState(false);
  const [antwortAuf, setAntwortAuf] = useState<ChatNachricht | null>(null);
  /** Schmale Breite: entweder Liste oder Verlauf (12.2). */
  const [zeigeListe, setZeigeListe] = useState(false);
  /** Offen heißt: eine neue Gruppe wird gerade angelegt. */
  const [neueGruppe, setNeueGruppe] = useState<{ name: string; wer: string[] } | null>(null);

  const liste = useQuery({
    queryKey: ['chat-liste'],
    queryFn: () => api.hole<ChatListe>('/chat/liste'),
    refetchInterval: 15_000,
  });

  const gruppe = useQuery({
    queryKey: ['chat-gruppe', raum],
    queryFn: () => api.hole<Gruppe>(`/chat/gruppe?raum=${encodeURIComponent(raum)}`),
    enabled: raum === 'team' || raum.startsWith('gruppe:'),
  });

  const ueberblick = useQuery({
    queryKey: ['chat-ueberblick', ''],
    queryFn: () => api.hole<ChatUeberblick>('/chat/ueberblick'),
    refetchInterval: 20_000,
  });

  /* Esc schließt – erst die Gruppeninfo, dann das Panel. */
  useEffect(() => {
    const auf = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (gruppeOffen) setGruppeOffen(false);
      else onSchliessen();
    };
    window.addEventListener('keydown', auf);
    return () => window.removeEventListener('keydown', auf);
  }, [gruppeOffen, onSchliessen]);

  const alleZeilen = useMemo(
    () => (liste.data ? [...liste.data.gruppen, ...liste.data.direkte] : []),
    [liste.data],
  );
  const aktiv = alleZeilen.find((z) => z.raum === raum) ?? liste.data?.gruppen[0] ?? null;

  /* --- Suche über alle Chats (12.9) --------------------------------------- */

  const suchtext = listenSuche.trim();
  const globalSuche = useQuery({
    queryKey: ['chat-suche', suchtext],
    queryFn: () => api.hole<{ treffer: Treffer[]; grenze: number }>(`/chat/suche-alles?q=${encodeURIComponent(suchtext)}`),
    enabled: suchtext.length >= 2,
  });

  const gefilterteZeilen = useMemo(() => {
    let z = alleZeilen;
    if (filter === 'ungelesen') z = z.filter((x) => x.ungelesen > 0 || x.raum === raum);
    if (suchtext.length >= 2) {
      z = z.filter((x) => x.name.toLowerCase().includes(suchtext.toLowerCase()));
    }
    return z;
  }, [alleZeilen, filter, suchtext, raum]);

  /** Sprung aus der Suche: Chat wechseln und die Nachricht anzeigen (12.9). */
  const [sprungZu, setSprungZu] = useState<number | null>(null);
  function trefferOeffnen(t: Treffer) {
    setRaum(t.raum);
    setSprungZu(t.id);
    setZeigeListe(false);
  }

  const ungelesenGesamt = liste.data?.ungelesenGesamt ?? 0;

  return (
    <div className="ch-schleier" onMouseDown={(e) => e.target === e.currentTarget && onSchliessen()}>
      <aside className={`ch-panel ${zeigeListe ? 'ch-zeige-liste' : 'ch-zeige-chat'}`} role="dialog" aria-label="Chat">
        {/* --- Links: die Chats (12.3) ---------------------------------- */}
        <div className="ch-liste">
          <div className="ch-liste-kopf">
            <span className="ch-liste-titel">Chats</span>
            {/* Gruppen anlegen darf, wer auch Rechte vergibt. */}
            {nutzer?.istAdmin && (
              <button
                type="button"
                className="ch-rund"
                title="Neue Gruppe"
                onClick={() => setNeueGruppe((n) => (n ? null : { name: '', wer: [] }))}
              >
                ＋
              </button>
            )}
            <button type="button" className="ch-rund" title="Schließen" onClick={onSchliessen}>
              ✕
            </button>
          </div>

          {neueGruppe && (
            <NeueGruppe
              entwurf={neueGruppe}
              personen={(ueberblick.data?.teilnehmer ?? []).filter((t) => !t.eigen).map((t) => t.name)}
              onAendern={setNeueGruppe}
              onFertig={(r) => {
                setNeueGruppe(null);
                setRaum(r);
                void queryClient.invalidateQueries({ queryKey: ['chat-liste'] });
              }}
            />
          )}

          <div className="ch-suchfeld">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.2-3.2" />
            </svg>
            <input
              value={listenSuche}
              placeholder="Chats und Nachrichten durchsuchen"
              onChange={(e) => setListenSuche(e.target.value)}
            />
            {listenSuche && (
              <button type="button" className="ch-such-x" title="Suche beenden" onClick={() => setListenSuche('')}>
                ✕
              </button>
            )}
          </div>

          <div className="ch-filter">
            <button
              type="button"
              className={`ch-pille ${filter === 'alle' ? 'aktiv' : ''}`}
              onClick={() => setFilter('alle')}
            >
              Alle
            </button>
            <button
              type="button"
              className={`ch-pille ${filter === 'ungelesen' ? 'aktiv' : ''}`}
              onClick={() => setFilter('ungelesen')}
            >
              Ungelesen · {ungelesenGesamt}
            </button>
          </div>

          <div className="ch-zeilen">
            {gefilterteZeilen.map((z) => (
              <Chatzeile
                key={z.raum}
                z={z}
                aktiv={z.raum === raum}
                onWahl={() => {
                  setRaum(z.raum);
                  setZeigeListe(false);
                }}
              />
            ))}
            {gefilterteZeilen.length === 0 && suchtext.length < 2 && (
              <span className="ch-leer">Kein Chat in dieser Auswahl.</span>
            )}

            {/* Treffer aus allen Chats, neueste zuerst (12.9). */}
            {suchtext.length >= 2 && (
              <div className="ch-treffer">
                <div className="ch-treffer-kopf">
                  Nachrichten · {globalSuche.data?.treffer.length ?? 0}
                  {(globalSuche.data?.treffer.length ?? 0) >= (globalSuche.data?.grenze ?? 40) && (
                    <span className="ch-treffer-note"> · die neuesten {globalSuche.data?.grenze}</span>
                  )}
                </div>
                {globalSuche.isLoading && <span className="ch-leer">Wird gesucht …</span>}
                {globalSuche.data?.treffer.length === 0 && !globalSuche.isLoading && (
                  <span className="ch-leer">Nichts gefunden.</span>
                )}
                {globalSuche.data?.treffer.map((t) => (
                  <button key={`${t.raum}-${t.id}`} type="button" className="ch-treffer-zeile" onClick={() => trefferOeffnen(t)}>
                    <span className="ch-treffer-ort">
                      {t.ort} · {t.autor}
                      <span className="ch-treffer-zeit">{listenZeit(t.zeitpunkt)}</span>
                    </span>
                    <span className="ch-treffer-text">
                      {t.ausschnitt.slice(0, t.von)}
                      <mark>{t.ausschnitt.slice(t.von, t.bis)}</mark>
                      {t.ausschnitt.slice(t.bis)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* --- Rechts: der Verlauf (12.5–12.11) ------------------------- */}
        {aktiv && (
          <Verlauf
            key={aktiv.raum}
            zeile={aktiv}
            ich={ich}
            personen={(ueberblick.data?.teilnehmer ?? []).map((t) => t.name)}
            ablageHinweis={
              aktiv.art === 'gruppe'
                ? ueberblick.data?.ablage.team ?? ''
                : ueberblick.data?.ablage.direkt ?? ''
            }
            sprungZu={sprungZu}
            onSprungFertig={() => setSprungZu(null)}
            antwortAuf={antwortAuf}
            onAntwort={setAntwortAuf}
            onGruppe={() => setGruppeOffen(true)}
            onZurueck={() => setZeigeListe(true)}
            onGelesen={() => {
              void queryClient.invalidateQueries({ queryKey: ['chat-liste'] });
              void queryClient.invalidateQueries({ queryKey: ['chat-ueberblick'] });
            }}
          />
        )}

        {gruppeOffen && gruppe.data && (
          <Gruppeninfo
            gruppe={gruppe.data}
            mitglieder={ueberblick.data?.teilnehmer ?? []}
            darfAendern={!!nutzer?.istAdmin}
            onSchliessen={() => setGruppeOffen(false)}
            onDirekt={(name) => {
              const r = [ich, name].sort((a, b) => a.localeCompare(b, 'de')).join(':');
              setRaum(r);
              setGruppeOffen(false);
            }}
            onGeaendert={() => void queryClient.invalidateQueries({ queryKey: ['chat-gruppe'] })}
          />
        )}
      </aside>
    </div>
  );
}

/* ---- Eine Zeile der Chat-Liste --------------------------------------------- */

function Chatzeile({ z, aktiv, onWahl }: { z: ChatZeile; aktiv: boolean; onWahl: () => void }) {
  return (
    <button type="button" className={`ch-zeile ${aktiv ? 'aktiv' : ''}`} onClick={onWahl}>
      <Gruppensymbol zeile={z} groesse={38} />
      <span className="ch-zeile-mitte">
        <span className="ch-zeile-oben">
          <span className="ch-zeile-name">
            {z.istTeam && (
              <span className="ch-nadel" title="Immer oben" aria-hidden="true">
                📌
              </span>
            )}
            {z.name}
          </span>
          <span className={`ch-zeile-zeit ${z.ungelesen > 0 ? 'ungelesen' : ''}`}>{listenZeit(z.zeitpunkt)}</span>
        </span>
        <span className="ch-zeile-unten">
          <span className="ch-zeile-vorschau">
            {z.eigenesLetztes && (
              <span className={`ch-haken ${z.letzteGelesen ? 'gelesen' : ''}`} aria-hidden="true">
                ✓✓
              </span>
            )}
            {z.vorschau ? (
              <>
                {z.art === 'gruppe' && z.vorschauWer ? `${z.vorschauWer}: ` : ''}
                {z.vorschau}
              </>
            ) : (
              <span className="ch-zeile-still">Noch keine Nachricht</span>
            )}
          </span>
          {z.ungelesen > 0 && <span className="ch-zahl">{z.ungelesen > 99 ? '99+' : z.ungelesen}</span>}
        </span>
      </span>
    </button>
  );
}

/** Der Avatar: Gruppensymbol oder Kürzel mit Anwesenheitspunkt (12.3). */
export function Gruppensymbol({ zeile, groesse }: { zeile: ChatZeile; groesse: number }) {
  if (zeile.art === 'direkt') {
    return (
      <span className="ch-avatar" style={{ width: groesse, height: groesse, fontSize: groesse * 0.31 }}>
        {zeile.kuerzel}
        {zeile.online && <span className="ch-punkt" title="In Kora angemeldet" />}
      </span>
    );
  }
  return <Symbolbild symbol={zeile.symbol || 'monogramm'} bildUrl={zeile.bildUrl} groesse={groesse} />;
}

/** Die sieben Vorlagen und das eigene Bild (12.4). */
export function Symbolbild({
  symbol,
  bildUrl,
  groesse,
}: {
  symbol: string;
  bildUrl: string;
  groesse: number;
}) {
  const stil = { width: groesse, height: groesse } as const;
  if (symbol === 'bild' && bildUrl) {
    return <img className="ch-gsymbol" style={stil} src={bildUrl} alt="" />;
  }
  if (symbol === 'logo') {
    return (
      <span className="ch-gsymbol hell" style={stil}>
        <img src="/eichenherz-logo.png" alt="" style={{ width: groesse * 0.62 }} />
      </span>
    );
  }
  const VORLAGEN: Record<string, { grund: string; zeichen: string }> = {
    monogramm: { grund: '#211E1C', zeichen: 'EH' },
    haus: { grund: '#746E65', zeichen: '⌂' },
    fenster: { grund: '#5E6C60', zeichen: '▤' },
    werkzeug: { grund: '#8B7B6E', zeichen: '⚒' },
    herz: { grund: '#6F7772', zeichen: '♥' },
    team: { grund: '#667B82', zeichen: '☰' },
  };
  const v = VORLAGEN[symbol] ?? VORLAGEN.monogramm!;
  return (
    <span className="ch-gsymbol" style={{ ...stil, background: v.grund, fontSize: groesse * 0.33 }}>
      {v.zeichen}
    </span>
  );
}

/* ---- Der Verlauf ----------------------------------------------------------- */

function Verlauf({
  zeile,
  ich,
  personen,
  ablageHinweis,
  sprungZu,
  onSprungFertig,
  antwortAuf,
  onAntwort,
  onGruppe,
  onZurueck,
  onGelesen,
}: {
  zeile: ChatZeile;
  ich: string;
  personen: string[];
  ablageHinweis: string;
  sprungZu: number | null;
  onSprungFertig: () => void;
  antwortAuf: ChatNachricht | null;
  onAntwort: (n: ChatNachricht | null) => void;
  onGruppe: () => void;
  onZurueck: () => void;
  onGelesen: () => void;
}) {
  const { nutzer } = useKora();
  /* Anpinnen ist Admin-Sache und gibt es nur in Gruppen (12.9). */
  const darfPinnen = !!nutzer?.istAdmin && zeile.art === 'gruppe';

  const [daten, setDaten] = useState<VerlaufAntwort | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [suchLeiste, setSuchLeiste] = useState(false);
  const [suche, setSuche] = useState('');
  const [treffer, setTreffer] = useState<number[]>([]);
  const [trefferNr, setTrefferNr] = useState(0);
  const [markiert, setMarkiert] = useState<number | null>(null);

  const rolle = useRef<HTMLDivElement>(null);
  const suchfeld = useRef<HTMLInputElement>(null);
  /** Abstand zum unteren Rand, über das Nachladen hinweg gemerkt (12.8). */
  const halten = useRef<number | null>(null);
  const amBoden = useRef(true);
  const [zeigePfeil, setZeigePfeil] = useState(false);

  /* --- Laden ------------------------------------------------------------- */

  const laden = useCallback(
    async (vor: number | null) => {
      setLaedt(true);
      try {
        const a = await api.hole<VerlaufAntwort>(
          `/chat/verlauf?raum=${encodeURIComponent(zeile.raum)}${vor ? `&vor=${vor}` : ''}`,
        );
        setDaten((alt) => {
          if (!alt || !vor) return a;
          /* Ältere davor – der Rest bleibt, damit nichts neu gezeichnet wird. */
          return { ...alt, nachrichten: [...a.nachrichten, ...alt.nachrichten], nochOffen: a.nochOffen, beginn: a.beginn };
        });
      } finally {
        setLaedt(false);
      }
    },
    [zeile.raum],
  );

  useEffect(() => {
    void laden(null);
  }, [laden]);

  /* Neue Nachrichten holen, ohne den gelesenen Teil anzufassen. */
  useEffect(() => {
    const takt = setInterval(async () => {
      const a = await api.hole<VerlaufAntwort>(`/chat/verlauf?raum=${encodeURIComponent(zeile.raum)}`);
      setDaten((alt) => {
        if (!alt) return a;
        const bekannt = new Set(alt.nachrichten.map((n) => n.id));
        const neue = a.nachrichten.filter((n) => !bekannt.has(n.id));
        const pin = { angepinnt: a.angepinnt, angepinntVon: a.angepinntVon };
        if (!neue.length) return { ...alt, ...pin };
        return { ...alt, nachrichten: [...alt.nachrichten, ...neue], ...pin };
      });
    }, 15_000);
    return () => clearInterval(takt);
  }, [zeile.raum]);

  /* --- Lesestand melden --------------------------------------------------- */

  const letzteId = daten?.nachrichten.at(-1)?.id ?? 0;
  useEffect(() => {
    if (!letzteId) return;
    void api
      .sende('/chat/gelesen-bis', { raum: zeile.raum, bisId: letzteId })
      .then(onGelesen)
      .catch(() => undefined);
    /* onGelesen ist stabil genug; der Lesestand hängt an der letzten ID. */
  }, [letzteId, zeile.raum]);

  /* --- Rollen: ans Ende, nachladen, Pfeil --------------------------------- */

  useLayoutEffect(() => {
    const el = rolle.current;
    if (!el || !daten) return;
    if (halten.current !== null) {
      /* Nach dem Einfügen älterer Nachrichten die Stelle halten (12.8). */
      el.scrollTop = el.scrollHeight - halten.current;
      halten.current = null;
      return;
    }
    if (amBoden.current) el.scrollTop = el.scrollHeight;
  }, [daten]);

  const beimRollen = () => {
    const el = rolle.current;
    if (!el) return;
    const untenAbstand = el.scrollHeight - el.scrollTop - el.clientHeight;
    amBoden.current = untenAbstand < 40;
    setZeigePfeil(untenAbstand > 160);
    if (el.scrollTop < 120 && !laedt && daten && daten.nochOffen > 0) {
      halten.current = el.scrollHeight - el.scrollTop;
      void laden(daten.nachrichten[0]?.id ?? null);
    }
  };

  const aelterLaden = () => {
    const el = rolle.current;
    if (!el || !daten || laedt) return;
    halten.current = el.scrollHeight - el.scrollTop;
    void laden(daten.nachrichten[0]?.id ?? null);
  };

  /* --- Sprung zu einer Nachricht (aus der Suche) -------------------------- */

  const zuNachricht = useCallback(
    async (id: number) => {
      const da = daten?.nachrichten.some((n) => n.id === id);
      if (!da) {
        setLaedt(true);
        try {
          const a = await api.hole<VerlaufAntwort>(
            `/chat/verlauf-bis?raum=${encodeURIComponent(zeile.raum)}&id=${id}`,
          );
          amBoden.current = false;
          setDaten(a);
        } finally {
          setLaedt(false);
        }
      }
      /* Nach dem Zeichnen hinrollen und 2,6 s rahmen (12.9). */
      window.setTimeout(() => {
        const ziel = rolle.current?.querySelector(`[data-nid="${id}"]`);
        ziel?.scrollIntoView({ block: 'center' });
        amBoden.current = false;
        setMarkiert(id);
        window.setTimeout(() => setMarkiert((m) => (m === id ? null : m)), 2600);
      }, 60);
    },
    [daten, zeile.raum],
  );

  useEffect(() => {
    if (sprungZu === null) return;
    void zuNachricht(sprungZu).then(onSprungFertig);
    /* Nur beim Wechsel des Ziels – sonst liefe der Sprung endlos. */
  }, [sprungZu]);

  /* --- Suche im Chat (12.10) ---------------------------------------------- */

  useEffect(() => {
    if (!suchLeiste || suche.trim().length < 2) {
      setTreffer([]);
      setTrefferNr(0);
      return;
    }
    let abgebrochen = false;
    void api
      .hole<{ ids: number[] }>(
        `/chat/suche-chat?raum=${encodeURIComponent(zeile.raum)}&q=${encodeURIComponent(suche.trim())}`,
      )
      .then((a) => {
        if (abgebrochen) return;
        setTreffer(a.ids);
        setTrefferNr(a.ids.length ? 1 : 0);
        if (a.ids[0]) void zuNachricht(a.ids[0]);
      });
    return () => {
      abgebrochen = true;
    };
    /* `zuNachricht` absichtlich außen vor – es hängt an `daten` und liefe sonst rund. */
  }, [suche, suchLeiste, zeile.raum]);

  function blaettern(richtung: 1 | -1) {
    if (!treffer.length) return;
    const neu = Math.min(treffer.length, Math.max(1, trefferNr + richtung));
    setTrefferNr(neu);
    const id = treffer[neu - 1];
    if (id) void zuNachricht(id);
  }

  useEffect(() => {
    if (suchLeiste) suchfeld.current?.focus();
  }, [suchLeiste]);

  /* --- Senden ------------------------------------------------------------- */

  /*
   * Reagieren lädt nur die neueste Seite nach – die Reaktion hängt an einer
   * Nachricht, die man gerade sieht, und die steht fast immer darin. Der
   * ganze Verlauf müsste dafür nicht neu geholt werden.
   */
  const reagieren = useMutation({
    mutationFn: (e: { nachrichtId: number; emoji: string }) => api.sende('/chat/reaktion', e),
    onSuccess: async () => {
      const a = await api.hole<VerlaufAntwort>(`/chat/verlauf?raum=${encodeURIComponent(zeile.raum)}`);
      setDaten((alt) => {
        if (!alt) return a;
        const neu = new Map(a.nachrichten.map((n) => [n.id, n]));
        return { ...alt, nachrichten: alt.nachrichten.map((n) => neu.get(n.id) ?? n) };
      });
    },
  });

  /*
   * Anpinnen und Lösen sind derselbe Knopf (12.9): Steht die Nachricht schon
   * oben, nimmt der Klick sie wieder herunter. Danach genügt der Verlauf neu –
   * die Leiste hängt an ihm.
   */
  const pinnen = useMutation({
    mutationFn: (id: number | null) =>
      id === null
        ? api.loesche(`/chat/anpinnen?raum=${encodeURIComponent(zeile.raum)}`)
        : api.sende('/chat/anpinnen', { raum: zeile.raum, nachrichtId: id }),
    onSuccess: async () => {
      const a = await api.hole<VerlaufAntwort>(`/chat/verlauf?raum=${encodeURIComponent(zeile.raum)}`);
      setDaten((alt) => (alt ? { ...alt, angepinnt: a.angepinnt, angepinntVon: a.angepinntVon } : a));
    },
  });

  const senden = useMutation({
    mutationFn: (e: { text: string; anhang: Anhangwahl | null }) =>
      api.sende<{ id: number }>('/chat/beitrag', {
        raum: zeile.raum,
        text: e.text,
        antwortAuf: antwortAuf?.id ?? null,
        anhang: e.anhang,
      }),
    onSuccess: () => {
      onAntwort(null);
      amBoden.current = true;
      void laden(null);
      onGelesen();
    },
  });

  const nachrichten = daten?.nachrichten ?? [];
  const trefferMenge = useMemo(() => new Set(treffer), [treffer]);

  return (
    <div className="ch-verlauf">
      {/* --- Kopf (12.5) ------------------------------------------------ */}
      <div className="ch-kopf">
        <button type="button" className="ch-zurueck" title="Zur Liste" onClick={onZurueck}>
          ‹
        </button>
        <button
          type="button"
          className="ch-kopf-wer"
          onClick={() => zeile.art === 'gruppe' && onGruppe()}
          disabled={zeile.art !== 'gruppe'}
        >
          <Gruppensymbol zeile={zeile} groesse={42} />
          <span className="ch-kopf-text">
            <span className="ch-kopf-name">{zeile.name}</span>
            <span className={`ch-kopf-unter ${zeile.art === 'direkt' && zeile.online ? 'online' : ''}`}>
              {zeile.art === 'gruppe'
                ? ['Du', ...personen.filter((p) => p !== ich)].join(', ')
                : zeile.online
                  ? 'online'
                  : 'nicht angemeldet · bekommt eine Mail'}
            </span>
          </span>
        </button>
        <span className="ch-kopf-knoepfe">
          <button
            type="button"
            className={`ch-rund ${suchLeiste ? 'aktiv' : ''}`}
            title="Im Chat suchen"
            onClick={() => {
              setSuchLeiste((s) => !s);
              setSuche('');
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.2-3.2" />
            </svg>
          </button>
          {zeile.art === 'gruppe' && (
            <button type="button" className="ch-rund" title="Gruppeninfo" onClick={onGruppe}>
              ⓘ
            </button>
          )}
        </span>
      </div>

      {/* --- Suchleiste (12.10) ------------------------------------------ */}
      {suchLeiste && (
        <div className="ch-suchleiste">
          <input
            ref={suchfeld}
            value={suche}
            placeholder={`In „${zeile.name}" suchen`}
            onChange={(e) => setSuche(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                blaettern(1);
              }
              if (e.key === 'Escape') setSuchLeiste(false);
            }}
          />
          <span className="ch-such-zaehler">
            {treffer.length ? `${trefferNr} von ${treffer.length}` : suche.trim().length >= 2 ? 'nichts' : ''}
          </span>
          <button type="button" className="ch-rund" title="Älterer Treffer" onClick={() => blaettern(1)}>
            ▲
          </button>
          <button type="button" className="ch-rund" title="Neuerer Treffer" onClick={() => blaettern(-1)}>
            ▼
          </button>
          <button type="button" className="ch-rund" title="Suche schließen" onClick={() => setSuchLeiste(false)}>
            ✕
          </button>
        </div>
      )}

      {/* --- Angepinnte Nachricht (12.5) --------------------------------- */}
      {daten?.angepinnt && zeile.art === 'gruppe' && (
        <div className="ch-pin-leiste">
          <button type="button" className="ch-pin" onClick={() => void zuNachricht(daten.angepinnt!.id)}>
            <span className="ch-pin-nadel">📌</span>
            <span className="ch-pin-text">{daten.angepinnt.text}</span>
            {daten.angepinntVon && (
              <span className="ch-pin-wer">
                angepinnt von {daten.angepinntVon === ich ? 'dir' : daten.angepinntVon}
              </span>
            )}
          </button>
          {/* Lösen darf nur, wer auch anpinnen darf – alle anderen sehen nur die Leiste. */}
          {darfPinnen && (
            <button
              type="button"
              className="ch-pin-weg"
              title="Nicht mehr anpinnen"
              onClick={() => pinnen.mutate(null)}
            >
              ✕
            </button>
          )}
        </div>
      )}

      {/* --- Die Blasen --------------------------------------------------- */}
      <div className="ch-rolle" ref={rolle} onScroll={beimRollen}>
        {daten && daten.nochOffen > 0 && (
          <button type="button" className="ch-aelter" onClick={aelterLaden} disabled={laedt}>
            {laedt ? 'Wird geladen …' : `Ältere Nachrichten laden · noch ${daten.nochOffen}`}
          </button>
        )}
        {daten && daten.nochOffen === 0 && daten.beginn && (
          <span className="ch-beginn">Beginn des Verlaufs · {tagLabel(daten.beginn)}</span>
        )}

        {nachrichten.map((n, i) => {
          const vorher = nachrichten[i - 1];
          /*
           * Die Datumspille entfällt, wenn direkt darüber schon „Beginn des
           * Verlaufs · <Datum>" steht – sonst steht derselbe Tag zweimal
           * untereinander.
           */
          const anfangNennt = !vorher && daten?.nochOffen === 0 && daten.beginn === n.tag;
          const neuerTag = (!vorher || vorher.tag !== n.tag) && !anfangNennt;
          const folge = !!vorher && vorher.autor === n.autor && vorher.tag === n.tag;
          const trennerHier = daten?.neuAb === n.id;
          const ungelesenZahl = nachrichten.filter((x) => x.id >= (daten?.neuAb ?? 0) && !x.eigen).length;
          return (
            <div key={n.id}>
              {neuerTag && <span className="ch-tag">{tagLabel(n.tag)}</span>}
              {trennerHier && (
                <span className="ch-ungelesen-trenner">
                  {ungelesenZahl} ungelesene {ungelesenZahl === 1 ? 'Nachricht' : 'Nachrichten'}
                </span>
              )}
              <Blase
                n={n}
                folge={folge}
                gruppe={zeile.art === 'gruppe'}
                markiert={markiert === n.id}
                gesucht={trefferMenge.has(n.id)}
                aktuellerTreffer={treffer[trefferNr - 1] === n.id}
                ich={ich}
                darfPinnen={darfPinnen}
                istAngepinnt={daten?.angepinnt?.id === n.id}
                onAntwort={() => onAntwort(n)}
                onZitat={(id) => void zuNachricht(id)}
                onReagieren={(emoji) => reagieren.mutate({ nachrichtId: n.id, emoji })}
                onPin={() => pinnen.mutate(daten?.angepinnt?.id === n.id ? null : n.id)}
              />
            </div>
          );
        })}
        {!daten && <span className="ch-leer">Wird geladen …</span>}
        {daten && nachrichten.length === 0 && (
          <span className="ch-leer">Noch keine Nachricht – schreib die erste.</span>
        )}
      </div>

      {zeigePfeil && (
        <button
          type="button"
          className="ch-runter"
          title="Zur neuesten Nachricht"
          onClick={() => {
            const el = rolle.current;
            if (!el) return;
            amBoden.current = true;
            el.scrollTop = el.scrollHeight;
          }}
        >
          ↓
        </button>
      )}

      {/* --- Verfasser (12.11) -------------------------------------------- */}
      <ChatVerfasser
        platzhalter={
          zeile.art === 'gruppe' ? 'Nachricht an alle · @Name erwähnt jemanden' : `Nachricht an ${zeile.name}`
        }
        ablageHinweis={ablageHinweis}
        personen={personen}
        antwortAuf={antwortAuf ? { autor: antwortAuf.autor, text: antwortAuf.text } : null}
        antwortFarbe={antwortAuf ? namensfarbe(antwortAuf.autor) : 'var(--eh-rahmen-schalter)'}
        onAntwortWeg={() => onAntwort(null)}
        onSenden={(text, anhang) => senden.mutate({ text, anhang })}
        onTippt={() => void api.sende('/chat/tippt', { raum: zeile.raum }).catch(() => undefined)}
      />
    </div>
  );
}

/* ---- Eine Blase ------------------------------------------------------------ */

/** Die sechs Reaktionen aus 12.8 – dieselbe Liste wie am Server. */
const REAKTIONEN = ['👍', '❤️', '😂', '😮', '🙏', '✅'];

function Blase({
  n,
  folge,
  gruppe,
  markiert,
  gesucht,
  aktuellerTreffer,
  ich,
  darfPinnen,
  istAngepinnt,
  onAntwort,
  onZitat,
  onReagieren,
  onPin,
}: {
  n: ChatNachricht;
  folge: boolean;
  gruppe: boolean;
  markiert: boolean;
  gesucht: boolean;
  aktuellerTreffer: boolean;
  ich: string;
  /** Nur Admins in Gruppen – Direktchats haben keinen Pin (12.9). */
  darfPinnen: boolean;
  istAngepinnt: boolean;
  onAntwort: () => void;
  onZitat: (id: number) => void;
  onReagieren: (emoji: string) => void;
  onPin: () => void;
}) {
  const [waehler, setWaehler] = useState(false);

  const klassen = [
    'ch-blase',
    n.eigen ? 'eigen' : 'fremd',
    folge ? 'folge' : 'erste',
    markiert || aktuellerTreffer ? 'markiert' : '',
    gesucht && !aktuellerTreffer ? 'gesucht' : '',
  ]
    .filter(Boolean)
    .join(' ');

  /*
   * Der Platzhalter am Textende hält die Stelle frei, an der die Uhrzeit
   * sitzt (12.6). Ohne ihn liefe der Text unter die Zeit – mit ihm bleibt
   * eine einzeilige Nachricht genau eine Zeile hoch.
   */
  const platz = n.eigen ? 64 : 44;

  return (
    <div className={`ch-reihe ${n.eigen ? 'eigen' : 'fremd'} ${folge ? 'folge' : ''}`} data-nid={n.id}>
      {/* Im Team-Chat steht links das Kürzel – Folgeblasen rücken gleich weit ein. */}
      {gruppe && !n.eigen && (
        <span className={`ch-avatar-klein ${folge ? 'leer' : ''}`} aria-hidden={folge}>
          {folge ? '' : n.autorKurz}
        </span>
      )}

      <span className="ch-blase-spalte">
        {gruppe && !n.eigen && !folge && (
          <span className="ch-absender" style={{ color: namensfarbe(n.autor) }}>
            {n.autor}
          </span>
        )}

        <span className={klassen}>
          {n.antwortAuf && (
            <button
              type="button"
              className="ch-zitat"
              style={{ borderLeftColor: namensfarbe(n.antwortAuf.autor) }}
              onClick={() => onZitat(n.antwortAuf!.id)}
            >
              <span className="ch-zitat-wer" style={{ color: namensfarbe(n.antwortAuf.autor) }}>
                {n.antwortAuf.autor}
              </span>
              <span className="ch-zitat-text">{n.antwortAuf.anfang}</span>
            </button>
          )}

          <span className="ch-text">
            {mitErwaehnungen(n.text, n.erwaehnt, ich)}
            <span className="ch-platz" style={{ width: platz }} aria-hidden="true" />
          </span>

          {n.anhaenge.map((a) => (
            <a key={a.url} className="ch-anhang" href={a.url} target="_blank" rel="noreferrer">
              📎 {a.dateiname}
            </a>
          ))}

          <span className="ch-zeit">
            {uhrzeit(n.zeitpunkt)}
            {n.eigen && (
              <span className={`ch-haken ${n.gelesenVonAllen ? 'gelesen' : ''}`} aria-hidden="true">
                ✓✓
              </span>
            )}
          </span>
        </span>

        {/* Der Chip sitzt leicht überlappend unter der Blase (12.8). */}
        {n.reaktionen.length > 0 && (
          <span className="ch-reaktionen">
            {n.reaktionen.map((r) => (
              <button
                key={r.emoji}
                type="button"
                className={`ch-reaktion ${r.wer.includes(ich) ? 'eigen' : ''}`}
                title={r.wer.map((w) => `${w} ${r.emoji}`).join(' · ')}
                onClick={() => onReagieren(r.emoji)}
              >
                {r.emoji}
                {r.wer.length > 1 && <span className="ch-reaktion-zahl">{r.wer.length}</span>}
              </button>
            ))}
          </span>
        )}
      </span>

      <span className="ch-werkzeuge">
        <button
          type="button"
          className="ch-werkzeug"
          title="Reagieren"
          onClick={() => setWaehler((w) => !w)}
        >
          🙂
        </button>
        <button type="button" className="ch-werkzeug" title="Antworten" onClick={onAntwort}>
          ↩
        </button>
        {darfPinnen && (
          <button
            type="button"
            className={`ch-werkzeug ${istAngepinnt ? 'gepinnt' : ''}`}
            title={istAngepinnt ? 'Nadel lösen' : 'Oben anpinnen · für alle sichtbar'}
            onClick={onPin}
          >
            📌
          </button>
        )}
        {waehler && (
          <span className="ch-waehler">
            {REAKTIONEN.map((e) => (
              <button
                key={e}
                type="button"
                className="ch-waehler-emoji"
                onClick={() => {
                  onReagieren(e);
                  setWaehler(false);
                }}
              >
                {e}
              </button>
            ))}
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * `@Name` hervorheben (12.7).
 *
 * Gesucht wird nach den Namen, die der Server beim Speichern aufgelöst hat –
 * nicht nach allem, was mit @ beginnt. Eine Mailadresse im Text ist keine
 * Erwähnung.
 */
function mitErwaehnungen(text: string, erwaehnt: string[], ich: string) {
  if (!erwaehnt.length) return text;
  const muster = new RegExp(`@(${erwaehnt.map((e) => e.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
  const teile: (string | JSX.Element)[] = [];
  let zuletzt = 0;
  let t: RegExpExecArray | null;
  while ((t = muster.exec(text)) !== null) {
    if (t.index > zuletzt) teile.push(text.slice(zuletzt, t.index));
    teile.push(
      <span key={`${t.index}`} className={`ch-erwaehnung ${t[1] === ich ? 'mich' : ''}`}>
        {t[0]}
      </span>,
    );
    zuletzt = t.index + t[0].length;
  }
  if (zuletzt < text.length) teile.push(text.slice(zuletzt));
  return teile;
}

/* ---- Eine neue Gruppe anlegen ---------------------------------------------- */

/**
 * Name und Mitglieder – mehr braucht eine Gruppe zum Entstehen nicht.
 *
 * Symbol und Beschreibung kommen danach in der Gruppeninfo. Wer sie anlegt,
 * ist immer dabei; das setzt der Server, nicht dieses Formular.
 */
function NeueGruppe({
  entwurf,
  personen,
  onAendern,
  onFertig,
}: {
  entwurf: { name: string; wer: string[] };
  personen: string[];
  onAendern: (e: { name: string; wer: string[] } | null) => void;
  onFertig: (raum: string) => void;
}) {
  const anlegen = useMutation({
    mutationFn: () =>
      api.sende<Gruppe>('/chat/gruppe', { name: entwurf.name.trim(), mitglieder: entwurf.wer }),
    onSuccess: (g) => onFertig(g.raum),
  });

  const umschalten = (name: string) =>
    onAendern({
      ...entwurf,
      wer: entwurf.wer.includes(name) ? entwurf.wer.filter((w) => w !== name) : [...entwurf.wer, name],
    });

  return (
    <div className="ch-neu">
      <input
        className="ch-neu-name"
        value={entwurf.name}
        placeholder="Name der Gruppe"
        autoFocus
        onChange={(e) => onAendern({ ...entwurf, name: e.target.value })}
      />
      <span className="ch-neu-kicker">Wer ist dabei?</span>
      <div className="ch-neu-wer">
        {personen.map((p) => (
          <button
            key={p}
            type="button"
            className={`ch-neu-person ${entwurf.wer.includes(p) ? 'aktiv' : ''}`}
            onClick={() => umschalten(p)}
          >
            {p}
          </button>
        ))}
      </div>
      <div className="ch-neu-fuss">
        <button type="button" className="ch-tertiaer" onClick={() => onAendern(null)}>
          Abbrechen
        </button>
        <button
          type="button"
          className="ch-neu-los"
          disabled={entwurf.name.trim().length < 2 || anlegen.isPending}
          onClick={() => anlegen.mutate()}
        >
          {anlegen.isPending ? 'Wird angelegt …' : 'Gruppe anlegen'}
        </button>
      </div>
    </div>
  );
}
